import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion'; // Import motion
import { supabase } from '../utils/supabase';

// Placeholder for Lucide icons - we'll need to install this package
// For now creating simple icon components
const Check = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const AlertCircle = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="8" x2="12" y2="12"></line>
    <line x1="12" y1="16" x2="12.01" y2="16"></line>
  </svg>
);

const ArrowRight = ({ size = 18, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

// Styled components - matching exactly with ShippingCalculator
const Container = styled.div`
  width: 100%;
`;

const FormWrapper = styled.div`
  background-color: white;
  border-radius: 24px;
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 680px; /* <-- REVERTED: Back to original width */
  overflow: hidden;
  /* --- MODIFICATION START --- */
  /* Changed margin to reduce top spacing */
  margin: 0.5rem auto 2rem; /* top=0.5rem, left/right=auto, bottom=2rem */
  /* --- MODIFICATION END --- */
  border: 1px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(5px);
`;

const Header = styled.div`
  background-color: var(--secondary-color);
  color: white;
  padding: 1.5rem 2rem; /* <-- CHANGED: Reduced vertical padding */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-image: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  position: relative;
  overflow: hidden;
  border-radius: 0 0 24px 24px; /* Add rounded bottom corners */

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0) 60%);
    border-radius: 0 0 24px 24px; /* Ensure gradient respects border radius */
  }
`;

const TitleWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Subtitle = styled.p`
  font-size: var(--text-md);
  color: rgba(255, 255, 255, 0.95);
  margin-top: 0.5rem; /* <-- CHANGED: Reduced margin-top */
  max-width: 550px;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-weight: 400;
  letter-spacing: 0.01em;
`;

const PoleIcon = styled.div`
  background: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem; /* <-- CHANGED: Reduced margin-bottom */
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 143, 0, 0.2);

  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    border-radius: 50%;
    box-shadow: 0 0 0 1px rgba(255, 143, 0, 0.15);
  }

  img {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  margin-bottom: 1.5rem; /* <-- CHANGED: Reduced margin-bottom */
  margin-left: auto;
  margin-right: auto;
  max-width: 560px;
  /* Removed internal padding specific to narrower layout */
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0 0.5rem;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20%;
  color: ${props => props.active ? 'var(--primary-color)' : props.completed ? 'var(--success-color)' : 'var(--gray-400)'};
`;

const StepCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: var(--rounded-full);
  margin-bottom: 0.125rem;
  ${props => props.active ? `
    background-color: var(--primary-light);
    border: 1px solid var(--primary-color);
    color: var(--primary-color);
  ` : props.completed ? `
    background-color: var(--success-light);
    border: 1px solid var(--success-color);
    color: var(--success-color);
  ` : `
    background-color: var(--gray-100);
    border: 1px solid var(--gray-300);
    color: var(--gray-400);
  `}
`;

const StepLabel = styled.span`
  font-size: 0.6rem;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  background-color: var(--gray-200);
  height: 0.3rem;
  border-radius: var(--rounded-full);
  overflow: hidden;
`;

const ProgressIndicator = styled.div`
  background-color: var(--primary-color);
  height: 0.3rem;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const FormContent = styled.div`
  padding: 2.5rem 3.5rem; /* <-- CHANGED: Reduced vertical padding */
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: transparent;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.75));
  backdrop-filter: blur(10px);
`;

const StepCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px; /* Keep content centered and readable */
`;

const StepTitle = styled.h2`
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StepDescription = styled.div`
  color: var(--gray-600);
  font-size: var(--text-md);
  margin-bottom: 1rem; /* <-- CHANGED: Reduced margin-bottom */
  text-align: center;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || 'repeat(2, 1fr)'};
  gap: ${props => props.gap || '1.5rem'}; /* <-- REVERTED: Back to original gap */
  margin-top: ${props => props.marginTop || '0.5rem'};
  justify-content: center;
  align-items: stretch;
  width: 100%;
  max-width: 500px;
`;

const Card = styled.div`
  padding: ${props => props.padding || '0.9rem 1.25rem'}; /* <-- REVERTED: Back to original padding */
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'rgba(52, 152, 219, 0.15)'};
  background-color: ${props => props.selected ? 'rgba(255, 143, 0, 0.1)' : 'rgba(255, 255, 255, 0.5)'}; /* Use primary color for selected background */
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.selected ? '0 0 0 3px rgba(255, 143, 0, 0.2)' : 'none'}; /* Use primary color for selected shadow */
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  backdrop-filter: blur(4px);

  &:hover {
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'rgba(52, 152, 219, 0.3)'}; /* Keep hover effect, but use primary if selected */
    background-color: ${props => props.selected ? 'rgba(255, 143, 0, 0.15)' : 'rgba(255, 255, 255, 0.7)'}; /* Darker primary background on hover if selected */
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 143, 0, 0.25); /* Use primary color for focus shadow */
    background-color: ${props => props.selected ? 'rgba(255, 143, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)'}; /* Darker primary background on focus if selected */
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem; /* <-- REVERTED: Back to original gap */
  height: 100%;
  justify-content: center;
  padding: 0.5rem; /* <-- REVERTED: Back to original padding */
`;

const CardImage = styled.img`
  width: ${props => props.width || '2rem'};
  height: ${props => props.height || '2rem'};
  object-fit: contain;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem; /* <-- REVERTED: Back to original margin */
`;

const CardLabel = styled.span`
  font-weight: 600;
  text-align: center;
  font-size: var(--text-sm);
  margin: 0.25rem 0; /* <-- REVERTED: Back to original margin */
`;

const CardDescription = styled.span`
  font-size: var(--text-xs);
  color: var(--gray-600);
  text-align: center;
  max-width: 180px;
  line-height: 1.4;
  margin-top: 0.25rem; /* <-- REVERTED: Back to original margin */
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem; /* <-- CHANGED: Reduced margin-bottom */
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
  color: var(--secondary-color);
  font-size: var(--text-sm);
`;

const Input = styled.input`
  width: 90%;
  padding: 0.9rem 1.25rem; /* <-- REVERTED: Back to original padding */
  border: 2px solid rgba(52, 152, 219, 0.15);
  border-radius: 8px;
  font-size: var(--text-md);
  text-align: center;
  box-shadow: none;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.5);
  display: block;
  margin: 0 auto;
  backdrop-filter: blur(4px);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
    background-color: rgba(255, 255, 255, 0.8);
  }

  &:hover {
    border-color: rgba(52, 152, 219, 0.3);
    background-color: rgba(255, 255, 255, 0.7);
  }
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.5);
  padding: 1.25rem 1rem; /* <-- REVERTED: Back to original padding */
  border-radius: var(--rounded-lg);
  border: 1px solid rgba(52, 152, 219, 0.1);
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
`;

const SliderLabel = styled.span`
  margin: 0 1rem; /* <-- REVERTED: Back to original margin */
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--gray-500);
  user-select: none; /* Prevent text selection */
`;

const Slider = styled.input`
  width: 100%;
  height: 0.5rem;
  background-color: var(--gray-300);
  border-radius: var(--rounded-full);
  appearance: none;
  cursor: pointer;
  border: none;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: var(--primary-color);
  }

  &::-moz-range-thumb {
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: var(--primary-color);
    border: none;
  }

  &:focus {
    outline: none;
  }
`;

const AlertBox = styled.div`
  padding: 1rem; /* <-- REVERTED: Back to original padding */
  background-color: ${props => props.warning ? 'var(--warning-light)' : props.error ? 'var(--error-light)' : 'var(--info-light)'};
  border: 1px solid ${props => props.warning ? 'var(--warning-color)' : props.error ? 'var(--error-color)' : 'var(--info-color)'};
  border-radius: var(--rounded-lg);
  display: flex;
  align-items: flex-start;
`;

const AlertIcon = styled.div`
  color: ${props => props.warning ? 'var(--warning-color)' : props.error ? 'var(--error-color)' : 'var(--info-color)'};
  margin-right: 0.5rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
`;

const AlertText = styled.div`
  font-size: var(--text-sm);
  color: ${props => props.warning ? 'var(--warning-text)' : props.error ? 'var(--error-text)' : 'var(--info-text)'};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: ${props => props.between ? 'space-between' : 'center'};
  align-items: center; /* Keep alignment */
  flex-wrap: wrap; /* Keep wrap */
  gap: 1rem; /* Keep gap */
  margin-top: 1.25rem; /* <-- ADDED: Margin above button rows */
`;

const ResetButton = styled.button`
  width: auto;
  min-width: 180px; /* <-- REVERTED: Back to original min-width */
  margin-top: 0; /* Reset margin-top, handled by ButtonRow gap/margin */
  display: block;
  padding: 0.8rem 1.5rem; /* <-- REVERTED: Back to original padding */
  background-color: transparent;
  color: var(--primary-color);
  font-weight: 500;
  border-radius: 8px;
  font-size: var(--text-sm);
  transition: all 0.3s ease;
  border: 1px solid var(--primary-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(52, 152, 219, 0.05), rgba(52, 152, 219, 0.1), rgba(52, 152, 219, 0.05));
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover {
    background-color: rgba(52, 152, 219, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);

    &::before {
      transform: translateX(100%);
    }
  }
`;

const Button = styled.button`
  width: 100%;
  max-width: 260px; /* <-- REVERTED: Back to original max-width */
  padding: 1.2rem 1.5rem; /* <-- REVERTED: Back to original padding */
  background: linear-gradient(to right, var(--primary-color), #2980b9);
  color: white;
  font-weight: 500;
  border-radius: 8px;
  font-size: var(--text-md);
  margin-top: 0; /* Reset margin-top, handled by ButtonRow gap/margin */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(52, 152, 219, 0.2); /* <-- REVERTED: Back to original shadow */
  letter-spacing: 0.3px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover {
    background: linear-gradient(to right, #2980b9, var(--primary-color));
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(52, 152, 219, 0.3); /* <-- REVERTED: Back to original hover shadow */

    &::before {
      transform: translateX(100%);
    }
  }

  &:disabled {
    background-color: var(--primary-light);
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${props => props.secondary && `
    width: auto;
    min-width: 180px; /* <-- REVERTED: Back to original min-width */
    display: block;
    padding: 0.8rem 1.5rem; /* <-- REVERTED: Back to original padding */
    background-color: transparent;
    color: var(--primary-color);
    font-weight: 500;
    border: 1px solid var(--primary-color);
    box-shadow: none;
    margin-top: 0; /* Ensure consistent margin handling */

    &:hover {
      background-color: rgba(52, 152, 219, 0.08);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(52, 152, 219, 0.15);
    }
  `}

  ${props => props.success && `
    background: linear-gradient(to right, var(--success-color), #219864);
    color: white;
    box-shadow: 0 10px 20px rgba(39, 174, 96, 0.2); /* <-- REVERTED: Back to original shadow */

    &:hover {
      background: linear-gradient(to right, #219864, var(--success-color));
      transform: translateY(-3px);
      box-shadow: 0 15px 30px rgba(39, 174, 96, 0.3); /* <-- REVERTED: Back to original hover shadow */
    }
  `}

  svg {
    margin-right: 0.75rem; /* <-- REVERTED: Back to original icon margin */
  }
`;

const ButtonIcon = styled.span`
  ${props => props.left && 'margin-right: 0.75rem;'} /* <-- REVERTED: Back to original icon margin */
  ${props => props.right && 'margin-left: 0.75rem;'} /* <-- REVERTED: Back to original icon margin */
`;

const ResultCard = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: var(--rounded-lg);
  padding: 1.5rem; /* <-- CHANGED: Reduced padding */
  margin-bottom: 1rem; /* <-- CHANGED: Reduced margin-bottom */
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08); /* <-- REVERTED: Back to original shadow */
  width: 100%;
  max-width: 540px;
  text-align: center;
  border: 1px solid rgba(52, 152, 219, 0.15);
  backdrop-filter: blur(8px);
`;

const DetailsList = styled.ul`
  margin-top: 0.5rem;
  padding-left: 1rem;
  & > *:not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const DetailItem = styled.li`
  font-size: var(--text-sm);
  color: var(--gray-600);
  line-height: 1.5;
`;



const SelectedPoleBadge = styled.div`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 1rem; /* <-- CHANGED: Reduced margin */
  text-align: center;
  background-color: rgba(52, 152, 219, 0.1);
  padding: 0.6rem 1.2rem; /* <-- REVERTED: Back to original padding */
  border-radius: 20px;
  border: 1px solid rgba(52, 152, 219, 0.2);
  display: inline-block;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  strong {
    color: var(--primary-color);
    font-weight: 700;
  }
`;

// Info icon component
const InfoIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// --- MODIFICATION START ---
// Added styled component for the divider line
const DividerLine = styled.div`
  height: 1px;
  width: 80%; /* Adjust width as needed */
  margin: 1.25rem auto; /* Vertical spacing and horizontal centering */
  background: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.08), transparent); /* Light gradient */
`;
// --- MODIFICATION END ---

const PoleConfigurator = ({ onOpenVideoModal }) => { // Accept onOpenVideoModal prop
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    bagLength: 100,
    bagWidth: 100,
    bagHeight: 110,
    loopType: 'Corner loop',
    loopLength: 30,
    poleType: null // Change default to null
  });

  const [result, setResult] = useState({
    poleLength: '',
    error: null,
    details: null
  });

  const poleTypes = [
    { id: 'springhead', name: 'Springhead', image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Springhead-section.avif', description: 'Most Versatile' },
    { id: 'flow', name: 'Flow', image: 'https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles.images/Flow-Section.avif', description: 'Flow Optimization' },
  ];

  const loopTypes = [
    { id: 'corner', name: 'Corner loop', image: 'https://i.imgur.com/1Yhrs8S.png', description: 'Standard corner loop option' },
    { id: 'cross', name: 'Cross corner loop', image: 'https://i.imgur.com/OdgHmZD.png', description: 'Enhanced stability for heavier loads' },
  ];

  // Validate the current step
  const validateStep = () => {
    if (step === 1) {
      // Validate pole type
      return !!formData.poleType;
    }

    if (step === 2) {
      // Validate bag dimensions
      const { bagLength, bagWidth, bagHeight } = formData;

      // Convert to numbers for validation and handle empty values
      const length = parseFloat(bagLength) || 0;
      const width = parseFloat(bagWidth) || 0;
      const height = parseFloat(bagHeight) || 0;

      if (!length || !width || !height) return false;

      // Use >= 1 for positive dimension check
      if (length < 1 || width < 1 || height < 1) return false;


      // Additional validation for Flow pole
      if (formData.poleType?.toLowerCase() === 'flow') { // Optional chaining
        if (length < 95 || length > 100 || width < 95 || width > 100) {
          return false;
        }
      }

      return true;
    }

    if (step === 3) {
      // Validate loop type
      return !!formData.loopType;
    }

    if (step === 4) {
      // Validate loop length
      const loopLengthNum = parseFloat(formData.loopLength) || 0;

      if (!loopLengthNum) return false;

      // Flow poles require minimum 20cm loop length
      if (formData.poleType?.toLowerCase() === 'flow') { // Optional chaining
        if (loopLengthNum < 20) {
           return false;
        }
      }

      // Ensure loop length is within slider bounds (15-50)
      if (loopLengthNum < 15 || loopLengthNum > 50) return false;


      return true;
    }

    return true;
  };

  const handleInputChange = (field, value) => {
    // For numeric fields, ensure we have a number or empty string
    let processedValue = value;
    if (['bagLength', 'bagWidth', 'bagHeight', 'loopLength'].includes(field)) {
      // If it's an empty string store as-is to allow clearing the field
      if (value === '') {
        processedValue = '';
      } else {
        // Otherwise ensure it's a number
        processedValue = Number(value);
        // Prevent negative numbers for dimensions by clamping to 0 or 1 minimum if needed
        if (['bagLength', 'bagWidth', 'bagHeight'].includes(field) && processedValue < 0) {
            processedValue = 0; // Or 1 if that's the absolute minimum valid input
        }
      }
    }


    setFormData(prev => {
      const newState = {
        ...prev,
        [field]: processedValue
      };

      // If pole type is changed, reset dimensions and loop configuration
      if (field === 'poleType') {
        newState.bagLength = 100;
        newState.bagWidth = 100;
        newState.bagHeight = 110;
        newState.loopType = 'Corner loop';
        newState.loopLength = 30;
      }

      return newState;
    });
  };

  const saveCalculation = async (calculatedLength) => {
    try {
      const email = localStorage.getItem('indus_user_email');
      if (!email) {
        console.log('No user email found, skipping calculation save');
        return;
      }

      const { error } = await supabase
        .from('pole_calculations')
        .insert({
          user_email: email,
          pole_type: formData.poleType,
          bag_length: parseFloat(formData.bagLength),
          bag_width: parseFloat(formData.bagWidth),
          bag_height: parseFloat(formData.bagHeight),
          loop_type: formData.loopType,
          loop_length: parseFloat(formData.loopLength),
          calculated_pole_length: calculatedLength
        });

      if (error) {
        console.error('Error saving calculation:', error);
      } else {
        console.log('Calculation saved successfully');
      }
    } catch (error) {
      console.error('Unexpected error saving calculation:', error);
    }
  };

  const calculatePoleLength = () => {
    try {
      const { bagLength, bagWidth, bagHeight, loopType, loopLength, poleType } = formData;

      if (!poleType) {
        throw new Error("Pole type must be selected");
      }

      // Ensure we have valid numbers for calculations
      const heightCm = parseFloat(bagHeight) || 0;
      const length = parseFloat(bagLength) || 0;
      const width = parseFloat(bagWidth) || 0;
      const loopLengthNum = parseFloat(loopLength) || 0;

      // Basic validation before calculation (using >= 1 for dimensions)
      if (heightCm < 1 || length < 1 || width < 1 || loopLengthNum < 1) {
          throw new Error("Please enter valid positive dimensions and loop length.");
      }


      // Additional validation for loop length bounds
      if (loopLengthNum < 15 || loopLengthNum > 50) {
          throw new Error(`Loop length must be between 15cm and 50cm. Provided: ${loopLengthNum}cm.`);
      }


      const baseHeight = heightCm * 10; // Convert cm to mm
      let poleLength;
      let details = [];

      // Check if it's Flow or standard pole type
      if (poleType.toLowerCase() === 'flow') {
        // Flow pole calculation
        if (length < 95 || length > 100 || width < 95 || width > 100) {
          throw new Error(`Flow pole requires bag dimensions between 95x95 and 100x100. Provided: ${length}x${width}.`);
        }

        const loopLengthMm = loopLengthNum * 10;
        if (loopLengthMm < 200) {
          throw new Error(`Flow pole requires loop length of at least 20cm. Provided: ${loopLengthNum}cm.`);
        }

        poleLength = baseHeight + loopLengthMm - 100;
        details = [
          `Base height: ${baseHeight}mm`,
          `Loop length: ${loopLengthMm}mm`,
          `Adjustment: -100mm`,
          `Total: ${poleLength}mm`
        ];
      } else {
        // Standard pole calculation (Lockhead, Springhead, Rolling Springhead)
        const loopTypeLower = loopType.toLowerCase();

        if (loopTypeLower === "cross corner loop") {
          poleLength = baseHeight + 150;
          details = [
            `Base height: ${baseHeight}mm`,
            `Cross corner addition: +150mm`,
            `Total: ${poleLength}mm`
          ];
        } else if (loopTypeLower === "corner loop") {
          let addition;

          if (loopLengthNum >= 35) {
            addition = 250;
          } else if (loopLengthNum >= 25) {
            addition = 200;
          } else if (loopLengthNum >= 15) { // Ensure loop length is at least min slider value
              addition = 150;
          }
           else {
             // This case should technically be caught by the earlier validation, but added for safety
             throw new Error(`Corner loop length must be at least 15cm. Provided: ${loopLengthNum}cm.`);
          }

          poleLength = baseHeight + addition;
          details = [
            `Base height: ${baseHeight}mm`,
            `Corner loop addition (${loopLengthNum}cm): +${addition}mm`,
            `Total: ${poleLength}mm`
          ];
        } else {
          throw new Error(`Invalid loop type: "${loopType}".`);
        }
      }

      // Final check for non-positive pole length
      if (poleLength <= 0) {
          throw new Error(`Calculated pole length (${poleLength}mm) is not valid based on inputs. Please check dimensions.`);
      }

      const poleLengthStr = `${poleLength}mm`;

      setResult({
        poleLength: poleLengthStr,
        error: null,
        details: details
      });

      // Save to database after successful calculation
      saveCalculation(poleLengthStr);
    } catch (error) {
      setResult({
        poleLength: '',
        error: error.message,
        details: null
      });
    }
  };

  // Recalculate pole length whenever form data changes AND the step requires calculation (avoids premature errors)
  useEffect(() => {
      // Always clear error when moving back before step 4 or if inputs change on step 4 making it potentially valid again
      if (step < 4 || (step === 4 && validateStep())) {
         setResult(prev => ({ ...prev, error: null }));
      }

      // Only attempt calculation if on step 4 or beyond AND the step is valid
      if (step >= 4 && validateStep()) {
          calculatePoleLength();
      } else if (step < 4) {
           // Reset result fully if going back before calculation is possible/relevant
           setResult({ poleLength: '', error: null, details: null });
      }
      // If step is 4 but invalid, the error will be cleared by the first condition, and no new calculation is triggered.
      // If step is >= 4 and invalid, calculatePoleLength might set an error if called (e.g., by goToNext before validation).
  }, [formData, step]); // Add step dependency

  const goToNext = () => {
    if (validateStep()) {
        if (step === 4) { // Calculate specifically before moving to step 5, ensures result is up-to-date
            calculatePoleLength();
            // Do not proceed if calculation itself resulted in an error
            // Check the result state directly after calling calculatePoleLength
            // Need to access the potentially updated state, useEffect might not have run yet.
            // A temporary variable or checking validation again might be safer.
            // Let's re-validate and check the temporary result from calculatePoleLength if possible
            // For simplicity, assume calculatePoleLength updates state synchronously enough for this check,
            // OR rely on the fact that validateStep() already covers input errors.
            // Let's check `result.error` AFTER the calculation call.
             setTimeout(() => { // Use timeout to check state after potential update
                 if (result.error) {
                     console.log("Calculation error prevents moving to next step:", result.error);
                     return;
                 }
                 if (step < 5) {
                     setStep(step + 1);
                 }
             }, 0);
             return; // Prevent immediate step increment before timeout check
        }
      if (step < 5) { // Move this inside the main if, but outside the step === 4 block
        setStep(step + 1);
      }
    } else {
        // If validation fails on step 4 when trying to proceed, ensure the calculation runs
        // to display any relevant errors immediately (like loop length issues).
        if (step === 4) {
            calculatePoleLength(); // Trigger calculation to show errors
        }
        console.log("Validation failed for step:", step); // Optional: log validation failure
    }
  };


  const renderProgressBar = () => {
    const steps = ['Pole Type', 'Bag Dimensions', 'Loop Type', 'Loop Length'];

    return (
      <ProgressBarContainer>
        <StepsContainer>
          {steps.map((label, index) => (
            <StepItem
              key={index}
              active={index + 1 === step}
              completed={index + 1 < step}
            >
              <StepCircle
                active={index + 1 === step}
                completed={index + 1 < step}
              >
                {index + 1 < step ? <Check size={14} /> : index + 1}
              </StepCircle>
              <StepLabel>{label}</StepLabel>
            </StepItem>
          ))}
        </StepsContainer>
        <ProgressBar>
          <ProgressIndicator progress={step * 25} />
        </ProgressBar>
      </ProgressBarContainer>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepCard>
            <StepTitle>Select Pole Type</StepTitle>
            <StepDescription>Choose the type of pole that fits your requirements.</StepDescription>

            <Grid>
              {poleTypes.map(type => (
                <Card
                  key={type.id}
                  selected={formData.poleType ? formData.poleType.toLowerCase() === type.name.toLowerCase() : false}
                  onClick={() => handleInputChange('poleType', type.name)}
                >
                  <CardContent>
                    <CardImage
                      src={type.image}
                      alt={type.name}
                      width="3rem"
                      height="3.5rem"
                    />
                    <CardLabel>{type.name}</CardLabel>
                    {type.description && <CardDescription>{type.description}</CardDescription>}
                  </CardContent>
                </Card>
              ))}
            </Grid>

            {/* Main Continue Button */}
            <ButtonRow style={{ justifyContent: 'center' }}>
                <Button
                    onClick={goToNext}
                    disabled={!validateStep()}
                >
                    Continue
                    <ButtonIcon right><ArrowRight size={18} /></ButtonIcon>
                </Button>
            </ButtonRow>

            {/* --- MODIFICATION START --- */}
            {/* Insert the divider line here */}
            <DividerLine />
            {/* --- MODIFICATION END --- */}


              {/* Smaller help card section */}
              <div style={{ // Outer container for the help section
                // marginTop: '1rem', // Removed margin-top, handled by DividerLine margin
                width: '100%',
                display: 'flex',
                flexDirection: 'column', // Stack text and button row
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem' // Gap between text and button row
              }}>
                {/* Text part */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}>
                  <InfoIcon size={14} style={{ color: 'var(--primary-color)' }} />
                  <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    color: 'var(--gray-600)',
                    letterSpacing: '0.01em'
                  }}>
                    Need help choosing a pole?
                  </span>
                </div>

                {/* Container for the two help buttons */}
                <div style={{
                  display: 'flex', // Arrange buttons horizontally
                  justifyContent: 'center', // Center buttons in the row
                  gap: '0.5rem', // Space between buttons
                  // Removed flex-wrap, buttons are small enough
                }}>
                  {/* Learn More Button */}
                  <button
                    onClick={() => window.setCurrentView('polesProductPage')}
                    style={{
                      padding: '0.35rem 0.7rem',
                      fontSize: '0.65rem',
                      fontWeight: '500',
                      color: 'white',
                      background: 'linear-gradient(to right, #FF8F00, #e67e00)', // Original color
                      border: 'none',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      boxShadow: '0 1px 3px rgba(255, 143, 0, 0.2)',
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.02em',
                      // Removed marginTop
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(255, 143, 0, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(255, 143, 0, 0.2)';
                    }}
                  >
                    Learn more
                    <ButtonIcon right><ArrowRight size={8} /></ButtonIcon>
                  </button>
                  {/* Watch Video Button */}
                  <button
                    onClick={() => onOpenVideoModal('1NmKahCW9NQ')} // Call prop with specific video ID
                    style={{
                      padding: '0.35rem 0.7rem',
                      fontSize: '0.65rem',
                      fontWeight: '500',
                      color: 'white',
                      background: 'var(--secondary-color)', // Use the secondary color from the header title
                      border: 'none',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      boxShadow: '0 1px 3px rgba(0, 51, 102, 0.3)', // Adjusted shadow for secondary color
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.02em',
                     // Removed marginTop
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 51, 102, 0.4)'; // Adjusted hover shadow for secondary color
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 51, 102, 0.3)'; // Adjusted shadow for secondary color
                    }}
                  >
                    Watch Video
                    <ButtonIcon right><ArrowRight size={8} /></ButtonIcon>
                  </button>
                </div>
              </div>
          </StepCard>
        );

      case 2:
        const isLengthInvalidForFlow = formData.poleType?.toLowerCase() === 'flow' && (parseFloat(formData.bagLength) < 95 || parseFloat(formData.bagLength) > 100);
        const isWidthInvalidForFlow = formData.poleType?.toLowerCase() === 'flow' && (parseFloat(formData.bagWidth) < 95 || parseFloat(formData.bagWidth) > 100);
        // Show Flow warning only if BOTH fields have values AND are outside the range
        const showFlowWarning = formData.poleType?.toLowerCase() === 'flow' &&
                                (formData.bagLength !== '' && formData.bagWidth !== '') &&
                                (isLengthInvalidForFlow || isWidthInvalidForFlow);


        return (
          <StepCard>
            <StepTitle>Enter Bag Dimensions</StepTitle>
            <StepDescription>Please provide the measurements of your bag in centimeters.</StepDescription>

            <Grid columns="repeat(3, 1fr)" gap="0.5rem">
              <FormGroup>
                <Label>Length (cm)</Label>
                <Input
                  type="number"
                  value={formData.bagLength}
                  onChange={(e) => handleInputChange('bagLength', e.target.value)}
                  min="1" // HTML5 min attribute for basic browser validation
                  style={(isLengthInvalidForFlow && formData.bagLength !== '') ? { borderColor: 'var(--error-color)', outlineColor: 'var(--error-color)' } : {}} // Highlight invalid field only if non-empty
                />
              </FormGroup>

              <FormGroup>
                <Label>Width (cm)</Label>
                <Input
                  type="number"
                  value={formData.bagWidth}
                  onChange={(e) => handleInputChange('bagWidth', e.target.value)}
                  min="1"
                  style={(isWidthInvalidForFlow && formData.bagWidth !== '') ? { borderColor: 'var(--error-color)', outlineColor: 'var(--error-color)' } : {}} // Highlight invalid field only if non-empty
                />
              </FormGroup>

              <FormGroup>
                <Label>Height (cm)</Label>
                <Input
                  type="number"
                  value={formData.bagHeight}
                  onChange={(e) => handleInputChange('bagHeight', e.target.value)}
                  min="1"
                   // Check for valid number > 0 for potential error styling
                   style={(parseFloat(formData.bagHeight) < 1 && formData.bagHeight !== '') ? { borderColor: 'var(--error-color)', outlineColor: 'var(--error-color)' } : {}}
                />
              </FormGroup>
            </Grid>

            <SelectedPoleBadge style={{ marginTop: '1rem' }}> {/* Reduced margin-top */}
              Selected Pole Type: <strong>{formData.poleType}</strong>
            </SelectedPoleBadge>

             {/* Warning for Flow pole dimensions outside 95-100 cm */}
             {showFlowWarning && (
                 <AlertBox error style={{ marginTop: '1rem' }}> {/* Changed to error type */}
                     <AlertIcon error><AlertCircle size={18} /></AlertIcon>
                     <AlertText error>
                         Flow poles require dimensions between 95x95 and 100x100 cm.
                     </AlertText>
                 </AlertBox>
             )}

           <ButtonRow between>
             <ResetButton
                onClick={() => { setStep(1); }} // Keep form data, just go back
              >
                ← Back
              </ResetButton>
              <Button
                onClick={goToNext}
                disabled={!validateStep()}
              >
                Continue
                <ButtonIcon right><ArrowRight size={18} /></ButtonIcon>
              </Button>
            </ButtonRow>
          </StepCard>
        );

      case 3:
        return (
          <StepCard>
            <StepTitle>Select Loop Type</StepTitle>
            <StepDescription>Choose the loop type that fits your requirements.</StepDescription>

            <Grid gap="1.5rem"> {/* Reverted gap */}
              {loopTypes.map(type => (
                <Card
                  key={type.id}
                  selected={formData.loopType.toLowerCase() === type.name.toLowerCase()}
                  onClick={() => handleInputChange('loopType', type.name)}
                >
                  <CardContent>
                    <CardImage
                      src={type.image}
                      alt={type.name}
                      width={type.id === 'cross' ? '3rem' : '2.8rem'}
                      height={type.id === 'cross' ? '2.5rem' : '2.2rem'}
                    />
                    <CardLabel>{type.name}</CardLabel>
                    <CardDescription>{type.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </Grid>

            <ButtonRow between>
              <ResetButton onClick={() => setStep(2)} > ← Back </ResetButton>
              <Button onClick={goToNext} disabled={!validateStep()} >
                Continue
                <ButtonIcon right><ArrowRight size={18} /></ButtonIcon>
              </Button>
            </ButtonRow>
          </StepCard>
        );

      case 4:
        const loopLengthNum = parseFloat(formData.loopLength);
        const isLoopLengthInvalid = (formData.loopLength !== '' && (loopLengthNum < 15 || loopLengthNum > 50)) ||
                                   (formData.poleType?.toLowerCase() === 'flow' && formData.loopLength !== '' && loopLengthNum < 20);

        let loopLengthError = null;
        if (formData.loopLength !== '') {
            if (loopLengthNum < 15) loopLengthError = 'Loop length must be at least 15 cm';
            else if (loopLengthNum > 50) loopLengthError = 'Loop length cannot exceed 50 cm';
            else if (formData.poleType?.toLowerCase() === 'flow' && loopLengthNum < 20) loopLengthError = 'Flow poles require a minimum loop length of 20 cm';
        }


        return (
          <StepCard>
            <StepTitle>Specify Loop Length</StepTitle>
            <StepDescription>Set the length of the loop in centimeters.</StepDescription>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <Label style={{ marginBottom: 0 }}>Loop Length</Label>
              <span style={{ fontSize: 'var(--text-lg)', fontWeight: '600', color: 'var(--primary-color)' }}>{formData.loopLength || '--'} cm</span>
            </div>

            <SliderContainer>
              <SliderLabel>15 cm</SliderLabel>
              <Slider
                type="range"
                min="15"
                max="50"
                step="1"
                value={formData.loopLength} // Ensure value is controlled, handle empty string case if necessary
                onChange={(e) => handleInputChange('loopLength', e.target.value)}
              />
              <SliderLabel>50 cm</SliderLabel>
            </SliderContainer>

            <div style={{ marginTop: '0.75rem' }}>
              <Input
                type="number"
                value={formData.loopLength}
                onChange={(e) => handleInputChange('loopLength', e.target.value)}
                // min="15" // Keep HTML5 min/max for browser hints but rely on JS validation
                // max="50"
                placeholder="Enter length (15-50 cm)"
                style={isLoopLengthInvalid ? { borderColor: 'var(--error-color)', outlineColor: 'var(--error-color)' } : {}} // Highlight invalid input
              />
            </div>

            {/* Display validation error */}
            {loopLengthError && (
              <AlertBox error style={{ marginTop: '1rem' }}>
                <AlertIcon error><AlertCircle size={18} /></AlertIcon>
                <AlertText error>{loopLengthError}</AlertText>
              </AlertBox>
            )}


             {/* Display calculation error from useEffect if present AND not already covered by input validation */}
             {result.error && !loopLengthError && step === 4 && (
                  <AlertBox error style={{ marginTop: '1rem' }}>
                      <AlertIcon error><AlertCircle size={18} /></AlertIcon>
                      <AlertText error>{result.error}</AlertText>
                  </AlertBox>
              )}

            <ButtonRow between>
              <ResetButton onClick={() => setStep(3)} > ← Back </ResetButton>
              {/* Disable button if basic validation fails OR if there's a calculation error displayed */}
              <Button onClick={goToNext} disabled={!validateStep() || (step === 4 && (!!result.error && !loopLengthError))}>
                View Result
                <ButtonIcon right><ArrowRight size={18} /></ButtonIcon>
              </Button>
            </ButtonRow>
          </StepCard>
        );

      case 5:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <StepCard>
              <ResultCard>
                <div style={{ textAlign: 'center', marginBottom: '1rem', borderBottom: '1px solid rgba(0, 51, 102, 0.15)', paddingBottom: '1rem' }}>
                  <h3 style={{ fontSize: 'var(--text-md)', fontWeight: '700', color: '#003366', margin: 0 }}>Summary & Result</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  {/* Result Summary Items */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(0, 0, 0, 0.06)', flexWrap: 'wrap', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--secondary-color)', fontSize: 'var(--text-sm)' }}>Pole Type:</span>
                    <span style={{ fontWeight: '700', fontSize: 'var(--text-sm)', textAlign: 'right' }}>{formData.poleType || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(0, 0, 0, 0.06)', flexWrap: 'wrap', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--secondary-color)', fontSize: 'var(--text-sm)' }}>Bag Dimensions:</span>
                    <span style={{ fontWeight: '700', fontSize: 'var(--text-sm)', textAlign: 'right' }}>{formData.bagLength} x {formData.bagWidth} x {formData.bagHeight} cm</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(0, 0, 0, 0.06)', flexWrap: 'wrap', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--secondary-color)', fontSize: 'var(--text-sm)' }}>Loop Configuration:</span>
                    <span style={{ fontWeight: '700', fontSize: 'var(--text-sm)', textAlign: 'right' }}>{formData.loopType}, {formData.loopLength} cm</span>
                  </div>
                  {/* Calculated Pole Length */}
                   <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', marginTop: '0.75rem', borderTop: '2px solid rgba(52, 152, 219, 0.15)', flexWrap: 'wrap', gap: '0.25rem' }}>
                    <span style={{ color: 'var(--primary-color)', fontSize: 'var(--text-sm)', fontWeight: '700' }}>Calculated Pole Length:</span>
                    <span style={{ fontWeight: '700', fontSize: 'var(--text-lg)', color: 'var(--primary-color)', textAlign: 'right' }}>{result.poleLength || '---'}</span>
                  </div>
                  {/* Disclaimer */}
                  <div style={{ marginTop: '1rem', backgroundColor: 'rgba(52, 152, 219, 0.05)', padding: '0.75rem', borderRadius: 'var(--rounded-sm)', textAlign: 'center', border: '1px solid rgba(52, 152, 219, 0.1)' }}>
                    <span style={{ color: 'var(--secondary-color)', fontSize: 'var(--text-sm)' }}>This calculation is based on our standard specifications for big bag pole compatibility.</span>
                  </div>
                </div>
              </ResultCard>

              {/* Error Display - Should not typically show here if validation prevents moving to step 5 with errors */}
              {result.error && (
                <AlertBox error style={{ marginTop: '0' }}> {/* Reduced margin top */}
                  <AlertIcon error><AlertCircle size={18} /></AlertIcon>
                  <AlertText error>{result.error}</AlertText>
                </AlertBox>
              )}

               {/* Calculation Details */}
               {!result.error && result.details && result.details.length > 0 && (
                    <div style={{ padding: '1rem', border: '1px solid var(--gray-200)', borderRadius: 'var(--rounded-md)', marginTop: '0', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}> {/* Removed margin-top */}
                    <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: '600', color: 'var(--secondary-color)', marginBottom: '0.5rem' }}>Calculation Details</h3>
                    <DetailsList>
                        {result.details.map((detail, i) => (
                        <DetailItem key={i}>{detail}</DetailItem>
                        ))}
                    </DetailsList>
                    </div>
                 )}

              {/* Reset Button */}
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <ResetButton
                  onClick={() => {
                    setStep(1);
                    setFormData({ // Reset form data
                      bagLength: 100,
                      bagWidth: 100,
                      bagHeight: 110,
                      loopType: 'Corner loop',
                      loopLength: 30,
                      poleType: null
                    });
                    setResult({ // Also reset result
                        poleLength: '',
                        error: null,
                        details: null
                    });
                  }}
                  style={{ margin: '0 auto' }} // Center button
                >
                  ← Start New Calculation
                </ResetButton>
              </div>
            </StepCard>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <FormWrapper>
        <Header>
          <PoleIcon>
            <img
              src="https://ruylodmxakyicasoszik.supabase.co/storage/v1/object/public/poles/Poles%20Section.avif"
              alt="Pole"
              style={{ height: '30px', width: 'auto' }}
            />
          </PoleIcon>
          <TitleWrapper>
            <Title>Pole Calculator</Title>
            <Subtitle>Calculate the perfect pole length for your big bag</Subtitle>
          </TitleWrapper>
        </Header>

        <FormContent>
          {renderProgressBar()}
          {renderStep()}
        </FormContent>
      </FormWrapper>
    </Container>
  );
};

export default PoleConfigurator;